import { Colors } from "@/enums/Colors";
import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";
import { LazoPaths } from "@/enums/LazoPaths";
import { useResponsive } from "@/hooks/useResponsive";
import CaretSVG from "@/images/data-room/caret.svg";
import { DataRoomFileSection } from "@/pages/OneDataRoomFilesPage";
import { DataRoomEntry } from "@/pages/OneDataRoomPage";
import { MenuItem } from "@/types/MenuItem";
import { ArrowForward, ExpandLess, ExpandMore, FolderOpen } from "@mui/icons-material";
import { Box, List, Typography, listItemClasses, styled, typographyClasses } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LazoContextMenu from "../common/LazoContextMenu";
import LazoTooltip from "../utils/LazoTooltip";
import { FileIcon } from "../utils/icons/FileIcon";
import { FilesIcon } from "../utils/icons/FilesIcon";
import { FolderNotch } from "../utils/icons/FolderNotch";
import { DATA_ROOM_STATIC_ID, ENTRIES_EXCLUDED_FROM_SELECTION } from "./DataRoomCategoriesDefines";

function getBackgroundColor(isMatch: boolean, selected: boolean): string {
  let finalColor = "inherit";
  if (isMatch) {
    finalColor = Colors.ACCENT_ORANGE_LIGHT;
  }
  if (selected) {
    finalColor = Colors.BUTTON_SECONDARY_HOVER_PURPLE;
  }
  return finalColor;
}

function getNodeIcon(type: GeneralFileNodeType, colorsArray: { [level: number]: string }, level: number) {
  const color = (colorsArray ? colorsArray[level] : DEFAULT_COLORS_ARRAY[level]) || "#000";
  if (type === GeneralFileNodeType.FILE) {
    return (
      <FilesIcon
        sx={{
          fill: color,
        }}
      />
    );
  }
  return <FolderNotch style={{ fill: color }} />;
}

export const DEFAULT_COLORS_ARRAY = {
  0: Colors.BLUE_4763F4,
  1: Colors.BUTTON_HOVER_PURPLE,
  2: Colors.GREEN_01A902,
  3: Colors.ORANGE_FF9A3D,
};

interface DataRoomEntryNodeProps {
  entry: DataRoomEntry;
  level: number;
  folderColors?: { [level: number]: string };
  closeAll?: boolean;
  setCloseAll?: Dispatch<SetStateAction<boolean>>;
  // Highlight matches and collapse them
  matches?: DATA_ROOM_STATIC_ID[];
  hoverColor?: string;
  showGo?: boolean;
  selected?: DataRoomEntry;
  setSelected?: Dispatch<SetStateAction<DataRoomEntry>>;
  onNodeClick?: () => void;
}

function DataRoomEntryNodeComponent({
  entry,
  level,
  closeAll,
  setCloseAll,
  folderColors,
  matches,
  hoverColor,
  showGo,
  selected,
  setSelected,
  onNodeClick,
}: DataRoomEntryNodeProps) {
  const [innerCollapsed, setInnerCollapsed] = useState(false);
  const [isMatch, setIsMatch] = useState(false);

  const isSelected = selected?.staticId === entry.staticId;
  const isFolder = entry.type === GeneralFileNodeType.FOLDER;

  const navigate = useNavigate();
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  useEffect(() => {
    if (level === 0 && setCloseAll !== undefined && closeAll) {
      setInnerCollapsed(false);
      setCloseAll(false);
    }
  }, [closeAll, level, setCloseAll]);

  useEffect(() => {
    if (matches !== null) {
      const m = matches?.includes(entry.staticId);
      setInnerCollapsed(m);
      setIsMatch(m);
    } else {
      setInnerCollapsed(false);
      setIsMatch(false);
    }
  }, [matches, entry.staticId]);

  useEffect(() => {
    // COLLAPSE SELECTED
    if (!selected || innerCollapsed) {
      return;
    }

    // Only proceed if this entry is the selected one
    if (selected.staticId !== entry.staticId) return;

    let stack = [entry];
    while (stack.length > 0) {
      let ent = stack.pop();
      if (ent.staticId === selected.staticId) {
        setInnerCollapsed(true);
        break;
      }
      if (ent.entries) {
        for (let e of ent.entries) {
          stack.push(e);
        }
      }
    }
  }, [selected?.staticId, entry.staticId, innerCollapsed]);

  function handleSelectClick() {
    if (ENTRIES_EXCLUDED_FROM_SELECTION.includes(entry.staticId) || !setSelected) {
      setInnerCollapsed((prev) => !prev);
      return;
    }
    if (selected?.staticId !== entry.staticId) {
      setSelected(entry);
      setInnerCollapsed(true);
    } else {
      setSelected(null);
      setInnerCollapsed(false);
    }
  }

  function handleClick() {
    if (!isFolder) return;
    handleSelectClick();

    // Call onNodeClick for mobile/tablet when selecting a node
    if (isMobileOrTablet && onNodeClick) {
      onNodeClick();
    }
  }

  const handleEnterFolder = () => {
    navigate(LazoPaths.ONE_DATA_ROOM_FILES, {
      state: {
        section: DataRoomFileSection.MANAGE,
        entry: entry,
      },
    });
    if (isMobileOrTablet && onNodeClick) {
      onNodeClick();
    }
  };

  const handleExpandCollapse = () => {
    setInnerCollapsed(!innerCollapsed);
  };

  // Create context menu items for mobile
  const contextMenuItems: MenuItem[] = [
    {
      id: "enter-folder",
      label: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FolderOpen fontSize="small" />
          <Typography fontSize="0.875rem">{t("directoryTree.enterFolder")}</Typography>
        </Box>
      ),
      onClick: handleEnterFolder,
    },
    ...(entry.entries && entry.entries.length > 0
      ? [
          {
            id: "expand-collapse",
            label: (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {innerCollapsed ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                <Typography fontSize="0.875rem">{innerCollapsed ? t("directoryTree.collapse") : t("directoryTree.expand")}</Typography>
              </Box>
            ),
            onClick: handleExpandCollapse,
          },
        ]
      : []),
  ];

  function handleGoClick() {
    navigate(LazoPaths.ONE_DATA_ROOM_FILES, {
      state: {
        section: DataRoomFileSection.MANAGE,
        entry: entry,
      },
    });
  }

  if (entry.hidden) {
    return null;
  }

  const nodeContent = (
    <Box
      className="entry-info-container"
      onClick={!isMobileOrTablet ? handleClick : undefined}
      sx={{
        backgroundColor: getBackgroundColor(isMatch, isSelected),
        ...(isFolder && {
          "&:hover": {
            cursor: "pointer",
            borderRadius: "0.5rem",
            ...(!isSelected && {
              backgroundColor: hoverColor ? hoverColor : Colors.ALMOST_WHITE,
            }),
          },
        }),
      }}
    >
      <img
        src={CaretSVG.src}
        alt={"caret-svg"}
        style={{
          opacity: entry.entries?.length > 0 ? 1 : 0,
          transform: innerCollapsed ? "rotate(90deg)" : "",
        }}
      />
      {getNodeIcon(entry.type, folderColors || DEFAULT_COLORS_ARRAY, level)}
      <LazoTooltip title={entry.name} placement="left">
        <Typography fontSize="0.875rem" fontWeight="400" lineHeight="19.6px">
          {entry.name}
        </Typography>
      </LazoTooltip>
      {entry.count !== undefined && entry.count > 0 && (
        <Box className="count-container">
          <FileIcon />
          <Typography>{entry.count}</Typography>
        </Box>
      )}
      {showGo && isFolder && !isMobileOrTablet && (
        <GoButton onClick={handleGoClick}>
          <ArrowForward />
        </GoButton>
      )}
    </Box>
  );

  return (
    <>
      {isMobileOrTablet && isFolder ? (
        <LazoContextMenu menuItems={contextMenuItems} disabled={ENTRIES_EXCLUDED_FROM_SELECTION.includes(entry.staticId)} triggerOnClick>
          {nodeContent}
        </LazoContextMenu>
      ) : (
        nodeContent
      )}
      {!closeAll && innerCollapsed && (
        <DataRoomRecursiveList
          key={entry.staticId + "-list"}
          folderColors={folderColors}
          entry={entry}
          level={level + 1}
          matches={matches}
          hoverColor={hoverColor}
          showGo={showGo}
          selected={selected}
          setSelected={setSelected}
          onNodeClick={onNodeClick}
        />
      )}
    </>
  );
}

const DataRoomEntryNode = React.memo(DataRoomEntryNodeComponent);

export function DataRoomRecursiveList({
  entry,
  level,
  closeAll,
  setCloseAll,
  folderColors,
  matches,
  hoverColor,
  showGo,
  selected,
  setSelected,
  onNodeClick,
}: DataRoomEntryNodeProps) {
  return (
    <DataRoomEntriesList level={level} key={entry.staticId}>
      {entry.entries?.map((e) => (
        <DataRoomEntryNode
          key={e.staticId + "-node"}
          entry={e}
          level={level}
          closeAll={closeAll}
          setCloseAll={setCloseAll}
          hoverColor={hoverColor}
          folderColors={folderColors}
          matches={matches}
          showGo={showGo}
          selected={selected}
          setSelected={setSelected}
          onNodeClick={onNodeClick}
        />
      ))}
    </DataRoomEntriesList>
  );
}

export const DataRoomEntriesList = styled(List, {
  shouldForwardProp: (prop) => prop !== "level",
})<{ level: number }>(({ level }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  padding: 0,
  width: "100%",
  [`& .${listItemClasses.root}`]: {
    "&:hover": {
      cursor: "pointer",
      borderRadius: "5px",
      backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    },
  },
  "& .entry-info-container": {
    position: "relative",
    paddingLeft: level * 10 + "px",
    minHeight: "2.125rem",
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
    borderRadius: "0.5rem",
    [`& .${typographyClasses.root}`]: {
      fontSize: "0.875rem",
      fontWeight: "400",
      overflow: "hidden",
      maxWidth: "85%",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: "19.6px",
    },
    "& .count-container": {
      display: "flex",
      alignItems: "center",
      gap: "0.125rem",
      "& .MuiSvgIcon-root": {
        fontSize: "1rem",
      },
      [`& .${typographyClasses.root}`]: {
        fontSize: "0.75rem",
        fontWeight: "600",
      },
    },
  },
}));

const GoButton = styled(Box)(() => ({
  position: "absolute",
  right: 0,
  cursor: "default",
  display: "flex",
  alignItems: "center",
  color: Colors.LAZO_PURPLE,
  minHeight: "2.125rem",
  borderRadius: "0rem 0.5rem 0.5rem 0rem",
  padding: "0 0.125rem",
  transition: "all 0.1s ease-in",
  ":hover": {
    cursor: "pointer",
    color: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    backgroundColor: Colors.LAZO_PURPLE,
  },
}));
